import React, { useRef, useEffect } from "react";
import Chart from "chart.js/auto";
import classNames from "classnames";
import useWindowSize from "shared/customHooks/windowSize";

interface MultiAxisLineChartProps {
  datasets: any;
  legend?: boolean;
  gradient?: boolean;
  canvasStyle?: any;
  labels?: any;
  responsive?: boolean;
  width?: number;
  height?: number;
}

const MultiAxisLineChart = ({
  datasets,
  legend,
  gradient,
  canvasStyle,
  labels,
  responsive,
  width,
  height,
}: MultiAxisLineChartProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);
  const screenWidth = useWindowSize();

  useEffect(() => {
    const ctx = chartRef.current?.getContext("2d");

    // Destroy previous chart instance if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    if (ctx) {
      // Function to create a gradient
      const createGradient = (color: string) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 200);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
        return gradient;
      };

      if (gradient) {
        datasets.forEach((dataset: any) => {
          // Apply the gradient to each dataset's backgroundColor
          dataset.backgroundColor = createGradient(dataset.borderColor);
        });
      }

      chartInstanceRef.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: labels ? labels : [],
          datasets: datasets,
        },
        options: {
          animations: {
            radius: {
              duration: 400,
              easing: "linear",
              loop: (context) => context.active,
            },
          },
          maintainAspectRatio: responsive ? responsive : true,
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                color: "#949596",
                font: {
                  size: screenWidth > 1400 ? 12 : 9,
                },
              },
            },
            y: {
              type: "linear",
              display: true,
              position: "left",
              grid: {
                display: true,
              },
              ticks: {
                color: "#949596",
                font: {
                  size: 9,
                },
              },
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          elements: {
            point: {
              radius: 5,
            },
          },
          plugins: {
            tooltip: {
              position: "nearest",
              mode: "index",
              intersect: false,
              callbacks: {
                title: function (tooltipItems) {
                  return tooltipItems[0].label;
                },
                labelColor: function (context: any) {
                  return {
                    borderColor: context?.dataset?.borderColor,
                    backgroundColor: context.dataset.borderColor,
                    borderWidth: 2,
                    borderDash: [2, 2],
                    borderRadius: 2,
                  };
                },
                label: function (context) {
                  const dataset = context.dataset;
                  const value = context.raw;
                  return `${dataset.label}: ${value}`;
                },
              },
              usePointStyle: true,
              caretPadding: 10,
            },
            legend: {
              display: legend,
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                font: {
                  size: 10,
                },
              },
            },
          },
        },
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [datasets, gradient, labels, screenWidth]);

  return (
    <canvas
      ref={chartRef}
      className={classNames(canvasStyle && canvasStyle)}
    ></canvas>
  );
};

export default MultiAxisLineChart;
